import { FC, useCallback, useMemo } from 'react'
import { Session, BadgeName } from 'src/app/types'
import { formatDate } from 'src/utils/helpers'
import BadgeSVG from 'src/components/BadgeSVG/BadgeSVG'
import ActionButton from '../ActionButton'
import EllipsesDropdown from '../EllipsesDropdown'
import { useAppDispatch, useAppSelector, useCreateCsv } from 'src/hooks'
import { setCurrentPage } from 'src/app/page'
import TagDropdown from '../Dropdown/TagDropdown'
import { setType } from 'src/app/modal'
import { selectSessionId, setGroupId } from 'src/app/session'
import { appApi, useUpdateSessionMutation } from 'src/app/appApiQueries'
import Spinner from '../Spinner'

const SessionCard: FC<Required<Session> & { styles?: string }> = ({
  name,
  groupId,
  dateCreated,
  totalBadges,
  allBadgesCounts,
  id,
  participants,
  styles,
}) => {
  const dispatch = useAppDispatch()
  const activeSessionId = useAppSelector(selectSessionId)
  const [updateSession] = useUpdateSessionMutation()
  const date = useCallback(() => formatDate(dateCreated), [dateCreated])
  const topBadges = useMemo(
    () =>
      Object.entries(allBadgesCounts)
        .sort(([_, a], [_1, b]) => b - a)
        .slice(0, 5) as [BadgeName, number][],
    [allBadgesCounts]
  )
  const { onExportToCSV, isLoading } = useCreateCsv({
    participantArr: Object.values(participants),
    name,
  })

  const onNavigate = useCallback(() => {
    dispatch(setCurrentPage({ page: 'Session', props: { id, groupId } }))
  }, [id, dispatch, groupId])

  const onDelete = useCallback(() => {
    dispatch(setType({ type: 'sessionDelete', props: { id, groupId } }))
  }, [dispatch, id, groupId])

  const onGroupChange = useCallback(
    (newGroupId: string) => {
      if (activeSessionId === id) {
        dispatch(setGroupId(newGroupId))
      } else {
        updateSession({
          session: {
            name,
            groupId: newGroupId,
            dateCreated,
            totalBadges,
            allBadgesCounts,
            id,
            participants,
          },
          oldGroupId: groupId,
        })
          .unwrap()
          // this will refetch all sessions
          .then(() => dispatch(appApi.util.invalidateTags(['AllSessions'])))
      }
    },
    [
      activeSessionId,
      allBadgesCounts,
      dateCreated,
      dispatch,
      groupId,
      id,
      name,
      participants,
      totalBadges,
      updateSession,
    ]
  )

  return (
    <div
      className={`group p-3 min-w-[280px] rounded-lg shadow-card bg-white hover:bg-lightGrey ${styles}`}
    >
      <div className="flex justify-between">
        <h3
          aria-label="Click to view session"
          onClick={onNavigate}
          className="text-darkBlack text-sm font-medium hover:cursor-pointer group-hover:underline truncate pb-1 pr-1"
        >
          {name}
        </h3>
        <p className="text-grey text-xs whitespace-nowrap group-hover:hidden">
          {date()}
        </p>
        <div className="gap-x-1 h-6 hidden group-hover:flex">
          <ActionButton
            ariaLabel="Export"
            padding="sm"
            type="primary"
            styles="w-14"
            onClick={onExportToCSV}
            disabled={isLoading}
          >
            {isLoading ? <Spinner className="h-6 stroke-white" /> : 'Export'}
          </ActionButton>
          <EllipsesDropdown id={id} type="primary" padding="sqsm">
            <button className="w-[52px] text-left" onClick={onDelete}>
              Delete
            </button>
          </EllipsesDropdown>
        </div>
      </div>

      <TagDropdown setSelectedOption={onGroupChange} selectedValue={groupId} />

      <div className="flex pt-2 text-grey text-sm gap-x-3 whitespace-nowrap">
        <p>{totalBadges} total badges</p>
        <p className="text-secondaryGreyHover">|</p>
        <div className="inline-flex">
          {topBadges.map(([name]) => (
            <p className="mr-3 last:mr-0" key={name}>
              <BadgeSVG name={name} />
            </p>
          ))}
        </div>
      </div>
    </div>
  )
}

export default SessionCard
